// 🔴 LE PARCOURS DE CRÉATION **CORE** — le socle OmniHub, ⛔ AUCUN métier dedans.
//
// ⚠️ POURQUOI IL EXISTE. Mesuré : le modèle `Dossier` a UN SEUL champ obligatoire
// (`activite`) et ⛔ AUCUN champ TEF requis — il EST déjà générique. Ce qui ⛔ ne
// l'était pas, c'est l'ÉCRAN : le wizard demandait GIR, RFR, statut d'occupation
// et « MaPrimeAdapt' » à TOUT tenant qui n'était pas OMV.
// 🔑 ⇒ On ⛔ ne crée PAS `DossierMario`. On ouvre le CORE tel qu'il est : client,
// site, contact. Les métiers ajoutent ENSUITE leurs extensions — pour Mario,
// FUITE et DEVIS, qui vivent dans `visite_profils`.
//
// 🔴 CE QU'IL ⛔ NE DEMANDE JAMAIS : MaPrimeAdapt' · ANAH · GIR · mandataire ·
// régime d'aides · RFR · statut d'occupation. Si un métier en a besoin un jour,
// il le DÉCLARE — ⛔ il ne l'hérite pas.
import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";

// 🔑 DEUX PRIMITIVES CORE, RÉUTILISÉES — ⛔ pas réécrites pour Mario.
// `BanAddressInput` existait (le wizard TEF s'en sert déjà) ; la recherche
// d'entreprise existait DUPLIQUÉE dans 3 écrans, extraite en primitive.
import BanAddressInput from "../../components/shared/BanAddressInput";
import EntrepriseSearchInput from "../../components/shared/EntrepriseSearchInput";
// 🔴 N°797 — LE TYPE DE CLIENT VIENT DU TENANT, ⛔ plus de deux boutons en dur.
//    MESURÉ : cet écran (création de dossier du Core) proposait « Particulier / Entreprise »,
//    donc ⛔ NI Syndic ⛔ NI Professionnel, alors que `mario_bat` déclare les trois
//    depuis la migration 064. Deux écrans lisaient déjà l'autorité, celui-ci ⛔ non :
//    c'est ce qui faisait voir DEUX listes différentes selon l'écran.
import { useConfigList } from "../../hooks/useConfigList";
import { hasDossierSite } from "../../utils/tenantProfile";
// 🔴 P0 SYNDIC — la NATURE du type (personne morale ou non) vient de la source
//    unique partagée avec la fiche client (`utils/clientFormat`, sur B2B_TYPES
//    contient `professionnel` ET `copropriete`). ⛔ Aucune liste de types ici,
//    ⛔ aucun test de tenant.
import { estPersonneMorale } from "../../utils/clientFormat";
import { enteteTenant } from "../../utils/enteteTenant";

/** « SIRET ou SIREN » saisi ⇒ {siret, siren} ; ⛔ jamais une valeur devinée.
 *  14 chiffres = SIRET (son SIREN = ses 9 premiers) ; 9 chiffres = SIREN. */
export function identifiantLegal(saisie) {
  const n = String(saisie || "").replace(/\s/g, "");
  if (!n) return { siret: undefined, siren: undefined };
  if (/^\d{14}$/.test(n)) return { siret: n, siren: n.slice(0, 9) };
  if (/^\d{9}$/.test(n)) return { siret: undefined, siren: n };
  return null;                                   // format illisible ⇒ refus explicite
}

export default function CreateDossierCore({ tenantId, activite, onBack,
                                            onSuccess, clientPreselectionneId }) {
  // 🔴 PARTICULIER | ENTREPRISE — déclaré par l'utilisateur, ⛔ pas déduit.
  const [type, setType] = useState("particulier");
  // 🔑 SOURCE UNIQUE : `metier_config_listes{tenant_id, type:"client_types"}`.
  // ⚠️ ⛔ AUCUN repli enrichi ici — c'est le doublon qu'on ferme (N°797). Si la liste
  //    n'est pas seedée, on garde les deux valeurs HISTORIQUES de cet écran pour ⛔ ne
  //    pas le rendre inutilisable, et ⛔ on ⛔ n'y ajoute PAS Syndic : une valeur
  //    nouvelle entre par la liste du tenant, ⛔ jamais par le code.
  const { items: typesClient } = useConfigList("client_types");
  const optionsType = typesClient.length
    ? typesClient.map((o) => [o.id, o.label])
    : [["particulier", "Particulier"], ["professionnel", "Entreprise"]];
  const [client, setClient] = useState({ nom: "", prenom: "", telephone: "",
                                         email: "", siret: "", siren: "" });
  // 🔴 P0 SYNDIC — l'identifiant légal SAISI (SIRET ou SIREN), visible et
  //    modifiable ; rempli aussi par le choix d'une entreprise au registre.
  const [identifiant, setIdentifiant] = useState("");
  const morale = estPersonneMorale(type);
  const [site, setSite] = useState({ adresse: "", code_postal: "", ville: "" });
  const siteRequis = hasDossierSite();
  // 🔴 N°(a) SÉPARATION CLIENTS / DOSSIERS — tenant qui a adopté le modèle
  // Client 1─N Site 1─N Dossier : « Nouveau dossier » CHOISIT un client EXISTANT,
  // ⛔ il n'en crée jamais un en passant. Créer le client est un geste À PART
  // (bouton « Créer un nouveau client », qui ne crée que le client).
  const [clientChoisi, setClientChoisi] = useState(null);
  const [creationClient, setCreationClient] = useState(false);
  const [recherche, setRecherche] = useState("");
  const [resultats, setResultats] = useState(null);
  const [sitesClient, setSitesClient] = useState(null);
  const [siteChoisi, setSiteChoisi] = useState("nouveau");
  const [notes, setNotes] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  // 🔑 MÊME IDIOME QUE LES AUTRES ÉCRANS — ⛔ mais SANS le repli `|| "tef"`
  // qu'ils portent (CreateDossierTefWizard l.73) : donner le tenant d'un AUTRE
  // métier faute de tenant courant est exactement ce qu'on vient de supprimer.
  const entetes = () => {
    const jt = localStorage.getItem("session_token");
    return { ...enteteTenant(tenantId), "Content-Type": "application/json",
             ...(jt && { Authorization: `Bearer ${jt}` }) };
  };

  // Lecture des sites du client choisi (siteRequis) — LECTURE seule.
  useEffect(() => {
    if (!siteRequis || !clientChoisi?.id) { setSitesClient(null); return undefined; }
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(`/api/metier/clients/${encodeURIComponent(clientChoisi.id)}/sites`,
                              { headers: entetes() });
        const j = r.ok ? await r.json() : {};
        if (!vivant) return;
        const liste = j.sites || [];
        setSitesClient(liste);
        setSiteChoisi("nouveau");
      } catch { if (vivant) setSitesClient([]); }
    })();
    return () => { vivant = false; };
  }, [siteRequis, clientChoisi?.id]);                           // eslint-disable-line react-hooks/exhaustive-deps

  // Client déjà choisi en amont (fiche client → « Créer un dossier ») : on le LIT.
  useEffect(() => {
    if (!siteRequis || !clientPreselectionneId) return;
    (async () => {
      try {
        const r = await fetch(`/api/metier/clients/${encodeURIComponent(clientPreselectionneId)}`,
                              { headers: entetes() });
        if (r.ok) setClientChoisi(await r.json());
      } catch { /* l'utilisateur choisit alors à la main */ }
    })();
  }, [siteRequis, clientPreselectionneId]);                     // eslint-disable-line react-hooks/exhaustive-deps

  // Recherche d'un client EXISTANT (LECTURE, tenant de la session côté serveur).
  useEffect(() => {
    const q = recherche.trim();
    if (!siteRequis || q.length < 2) { setResultats(null); return undefined; }
    let vivant = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/metier/clients/search?q=${encodeURIComponent(q)}&limit=8`,
                              { headers: entetes() });
        const j = r.ok ? await r.json() : [];
        if (vivant) setResultats(Array.isArray(j) ? j : []);
      } catch { if (vivant) setResultats([]); }
    }, 250);
    return () => { vivant = false; clearTimeout(t); };
  }, [siteRequis, recherche]);                                  // eslint-disable-line react-hooks/exhaustive-deps

  // 🔴 L'ACTIVITÉ VIENT DU TENANT, DÉCLARÉE — ⛔ jamais devinée ici.
  // Sans elle, on refuse : créer un dossier sans savoir de quel métier il
  // relève, c'est exactement ce qu'on vient de supprimer.
  if (!activite) {
    return (
      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)",
                    borderRadius: 12, padding: 16 }}
           data-testid="creation-core-sans-activite">
        <div style={{ color: "var(--oh-text)", fontWeight: 500, marginBottom: 8 }}>
          Activité du dossier non configurée
        </div>
        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                      lineHeight: 1.6 }}>
          Ce métier a un parcours de création, mais l'activité que portent ses
          dossiers n'est pas déclarée (<code>config.dossier_activite</code>).
          Aucune activité n'est choisie à sa place.
        </div>
      </div>
    );
  }

  // ── LE GESTE « CLIENT » — par l'API normale, ⛔ jamais en base.
  // Renvoie l'id du client créé. Partagé par le parcours historique (client puis
  // dossier d'un seul geste) et par « Créer un nouveau client » (client SEUL).
  const posterClient = async () => {
    // 🔴 P0 SYNDIC / ENTREPRISE — ⛔ on ne perd NI le nom NI l'identifiant légal.
    let legal = { siret: undefined, siren: undefined };
    if (morale) {
      legal = identifiantLegal(identifiant);
      if (legal === null) {
        throw new Error("SIRET (14 chiffres) ou SIREN (9 chiffres) illisible.");
      }
      // Un SIRET/SIREN tapé dans le champ NOM sans choisir l'entreprise proposée
      // serait enregistré COMME NOM (mesuré) : on refuse et on le DIT.
      if (identifiantLegal(client.nom) && /^\d+$/.test(client.nom.replace(/\s/g, ""))) {
        throw new Error("Le nom ne peut pas être un SIRET/SIREN : choisissez "
                        + "l'entreprise proposée ou saisissez son nom.");
      }
    }
    const rc = await fetch("/api/metier/clients", {
      method: "POST",
      headers: entetes(),
      body: JSON.stringify({
        nom: client.nom.trim(),
        prenom: (!morale && client.prenom.trim()) || undefined,
        telephone: client.telephone.trim() || undefined,
        email: client.email.trim() || undefined,
        type_client: type,
        // personne morale : le nom EST la raison sociale (même règle que la fiche client)
        raison_sociale: morale ? client.nom.trim() : undefined,
        siret: morale ? legal.siret : undefined,
        siren: morale ? legal.siren : undefined,
      }),
    });
    const jc = await rc.json().catch(() => ({}));
    if (!rc.ok) {
      // ⚠️ Le refus de doublon est un RÉSULTAT, ⛔ pas un obstacle : on le
      // MONTRE tel quel. ⛔ Jamais de `force=true` pour passer outre.
      throw new Error(jc?.detail?.message || jc?.detail
                      || `Création du client refusée (${rc.status})`);
    }
    return jc;
  };

  // Créer le CLIENT seul (tenant adoptant) : le client créé est SÉLECTIONNÉ, le dossier
  // reste à créer — c'est un second geste, ⛔ jamais enchaîné.
  const creerClientSeul = async () => {
    setErreur(null);
    setOccupe(true);
    try {
      const jc = await posterClient();
      const id = jc.id || jc?.client?.id;
      setClientChoisi({ ...(jc.client || jc), id });
      setCreationClient(false);
      setClient({ nom: "", prenom: "", telephone: "", email: "", siret: "", siren: "" });
      setIdentifiant("");
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOccupe(false);
    }
  };

  // Tenant adoptant : le dossier se crée SUR un client EXISTANT et SUR un site —
  // l'un des siens, ou une nouvelle adresse (nouveau site de CE client).
  // ⛔ Aucun POST /clients ici.
  const creerSurClientExistant = async () => {
    setErreur(null);
    setOccupe(true);
    try {
      let siteId = siteChoisi !== "nouveau" ? siteChoisi : undefined;
      if (!siteId) {
        const rs = await fetch(`/api/metier/clients/${encodeURIComponent(clientChoisi.id)}/sites`, {
          method: "POST",
          headers: entetes(),
          body: JSON.stringify({ adresse: site.adresse.trim(), ville: site.ville.trim(),
                                 code_postal: site.code_postal.trim() || undefined }),
        });
        const js = await rs.json().catch(() => ({}));
        if (!rs.ok) {
          throw new Error(js?.detail || `Création du site refusée (${rs.status})`);
        }
        siteId = js.id;
      }
      const rd = await fetch("/api/metier/dossiers", {
        method: "POST",
        headers: entetes(),
        body: JSON.stringify({
          client_id: clientChoisi.id,
          site_id: siteId,
          activite,
          notes: notes.trim() || undefined,
        }),
      });
      const jd = await rd.json().catch(() => ({}));
      if (!rd.ok) {
        throw new Error(jd?.detail?.message || jd?.detail
                        || `Création du dossier refusée (${rd.status})`);
      }
      onSuccess && onSuccess(jd.dossier || jd);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOccupe(false);
    }
  };

  const creer = async () => {
    if (siteRequis) return creerSurClientExistant();
    setErreur(null);
    setOccupe(true);
    try {
      // ── 1 · LE CLIENT
      const jc = await posterClient();
      const clientId = jc.id || jc?.client?.id;

      // ── 2 · LE DOSSIER CORE — activité DÉCLARÉE, ⛔ aucun champ métier.
      // (Tenant non adoptant : aucun site, requête INCHANGÉE.)
      const rd = await fetch("/api/metier/dossiers", {
        method: "POST",
        headers: entetes(),
        body: JSON.stringify({
          client_id: clientId,
          activite,
          adresse_travaux: site.adresse.trim() || undefined,
          code_postal_travaux: site.code_postal.trim() || undefined,
          ville_travaux: site.ville.trim() || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      const jd = await rd.json().catch(() => ({}));
      if (!rd.ok) {
        throw new Error(jd?.detail?.message || jd?.detail
                        || `Création du dossier refusée (${rd.status})`);
      }
      onSuccess && onSuccess(jd.dossier || jd);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOccupe(false);
    }
  };

  const champ = (v, set, ph, tid, htmlType = "text") => (
    <input value={v} onChange={(e) => set(e.target.value)} placeholder={ph}
           type={htmlType} aria-label={ph} data-testid={tid}
           style={{ height: 36, borderRadius: 8, padding: "0 10px",
                    border: "1px solid var(--oh-border)",
                    background: "var(--oh-bg-surface)", color: "var(--oh-text)",
                    width: "100%" }} />
  );

  const siteSaisi = !!(site.adresse.trim() && site.ville.trim());
  const peutCreerDossier = !occupe && (siteRequis
    ? !!clientChoisi && (siteChoisi !== "nouveau" || siteSaisi)
    : !!client.nom.trim());

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}
         data-testid="creation-core">
      <div style={{ display: "flex", alignItems: "center", gap: 12,
                    marginBottom: 16 }}>
        {onBack && (
          <button onClick={onBack} aria-label="Retour"
                  style={{ background: "transparent", border: "none",
                           color: "var(--oh-text-secondary)" }}>
            <ArrowLeft size={18} />
          </button>
        )}
        <div data-ecran="creation-dossier" style={{ color: "var(--oh-text)", fontWeight: 500, fontSize: 18 }}>
          Nouveau dossier
        </div>
      </div>

      {erreur && (
        <div style={{ background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", borderRadius: 8,
                      padding: 10, marginBottom: 12, fontSize: 13 }}
             data-testid="creation-core-erreur">{erreur}</div>
      )}

      {siteRequis && !creationClient && (
        <div style={{ background: "var(--oh-bg-elevated)",
                      border: "0.5px solid var(--oh-border)", borderRadius: 12,
                      padding: 16, marginBottom: 12 }}
             data-testid="core-client-existant">
          <div style={{ fontWeight: 500, marginBottom: 12 }}>Client</div>
          {clientChoisi ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, color: "var(--oh-text)" }}
                   data-testid="core-client-choisi">
                {[clientChoisi.prenom, clientChoisi.nom].filter(Boolean).join(" ")
                  || clientChoisi.raison_sociale}
                {clientChoisi.reference && (
                  <span style={{ color: "var(--oh-text-muted)", marginLeft: 8,
                                 fontSize: 12 }}>{clientChoisi.reference}</span>
                )}
              </div>
              <button type="button" onClick={() => { setClientChoisi(null); setRecherche(""); }}
                      data-testid="core-client-changer"
                      style={{ height: 32, borderRadius: 8, padding: "0 12px",
                               background: "transparent",
                               border: "1px solid var(--oh-border)",
                               color: "var(--oh-text-secondary)" }}>
                Changer de client
              </button>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {champ(recherche, setRecherche, "Rechercher un client existant (nom, téléphone, email)",
                     "core-client-recherche")}
              {resultats && resultats.length > 0 && (
                <div style={{ display: "grid", gap: 4 }}>
                  {resultats.map((c) => (
                    <button key={c.id} type="button" onClick={() => setClientChoisi(c)}
                            data-testid={`core-client-resultat-${c.id}`}
                            style={{ textAlign: "left", minHeight: 36, borderRadius: 8,
                                     padding: "4px 10px", background: "transparent",
                                     border: "1px solid var(--oh-border)",
                                     color: "var(--oh-text)" }}>
                      {[c.prenom, c.nom].filter(Boolean).join(" ") || c.raison_sociale}
                      <span style={{ color: "var(--oh-text-muted)", marginLeft: 8,
                                     fontSize: 12 }}>
                        {[c.reference, c.telephone || c.email].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {resultats && resultats.length === 0 && (
                <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}
                     data-testid="core-client-aucun">
                  Aucun client ne correspond.
                </div>
              )}
              <button type="button" onClick={() => setCreationClient(true)}
                      data-testid="core-client-nouveau"
                      style={{ height: 36, borderRadius: 8, background: "transparent",
                               border: "1px solid var(--oh-accent)",
                               color: "var(--oh-accent-text)" }}>
                Créer un nouveau client
              </button>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 12,
                            lineHeight: 1.6 }}>
                Un dossier se crée sur un client existant. Créer le client est
                une étape à part : le dossier se crée ensuite.
              </div>
            </div>
          )}
        </div>
      )}

      {(!siteRequis || creationClient) && (
      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)", borderRadius: 12,
                    padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 12 }}>
          {creationClient ? "Nouveau client" : "Client"}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {optionsType.map(([v, lbl]) => (
            <button key={v} type="button" onClick={() => setType(v)}
                    data-testid={`core-type-${v}`}
                    style={{ flex: 1, height: 36, borderRadius: 8,
                             border: "1px solid " + (type === v
                               ? "var(--oh-accent)" : "var(--oh-border)"),
                             background: type === v
                               ? "var(--oh-accent-bg)" : "transparent",
                             color: type === v
                               ? "var(--oh-accent-text)" : "var(--oh-text-secondary)" }}>
              {lbl}
            </button>
          ))}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {morale && (
            <EntrepriseSearchInput value={client.nom} testId="core-entreprise"
              placeholder={type === "copropriete" ? "Nom du syndic (ou SIRET à rechercher)"
                                                  : "Nom de l'entreprise (ou SIRET à rechercher)"}
              onChange={(v) => setClient({ ...client, nom: v })}
              onPick={(e) => { setClient({ ...client, nom: e.nom,
                                           siret: e.siret, siren: e.siren });
                               setIdentifiant(e.siret || e.siren || "");
                               if (e.adresse) setSite({ adresse: e.adresse,
                                 code_postal: e.code_postal, ville: e.ville }); }} />
          )}
          {morale && champ(identifiant, setIdentifiant, "SIRET ou SIREN",
                           "core-client-identifiant")}
          {type === "particulier"
            && champ(client.nom, (v) => setClient({ ...client, nom: v }),
                     "Nom", "core-client-nom")}
          {type === "particulier"
            && champ(client.prenom, (v) => setClient({ ...client, prenom: v }),
                     "Prénom", "core-client-prenom")}
          {champ(client.telephone, (v) => setClient({ ...client, telephone: v }),
                 "Téléphone", "core-client-tel", "tel")}
          {champ(client.email, (v) => setClient({ ...client, email: v }),
                 "Email", "core-client-email", "email")}
        </div>
        {creationClient && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="button" onClick={() => { setCreationClient(false); setErreur(null); }}
                    data-testid="core-client-annuler"
                    style={{ height: 36, borderRadius: 8, padding: "0 16px",
                             background: "transparent",
                             border: "1px solid var(--oh-border)",
                             color: "var(--oh-text-secondary)" }}>
              Annuler
            </button>
            <button type="button" onClick={creerClientSeul}
                    disabled={!client.nom.trim() || occupe}
                    data-testid="core-client-creer"
                    style={{ flex: 1, height: 36, borderRadius: 8, border: "none",
                             fontWeight: 500,
                             background: client.nom.trim() && !occupe
                               ? "var(--oh-accent-solid)" : "var(--oh-bg-surface)",
                             color: client.nom.trim() && !occupe
                               ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)" }}>
              {occupe ? <Loader2 size={16} className="animate-spin" />
                      : "Créer le client"}
            </button>
          </div>
        )}
      </div>
      )}

      {!creationClient && (!siteRequis || clientChoisi) && (
      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)", borderRadius: 12,
                    padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 12 }}>
          Site de l'intervention
        </div>
        {siteRequis && (sitesClient || []).length > 0 && (
          <div style={{ display: "grid", gap: 4, marginBottom: 12 }}
               role="radiogroup" aria-label="Site du dossier">
            {sitesClient.map((st) => (
              <button key={st.id} type="button" role="radio"
                      aria-checked={siteChoisi === st.id}
                      onClick={() => setSiteChoisi(st.id)}
                      data-testid={`core-site-existant-${st.id}`}
                      style={{ textAlign: "left", minHeight: 36, borderRadius: 8,
                               padding: "4px 10px",
                               border: "1px solid " + (siteChoisi === st.id
                                 ? "var(--oh-accent)" : "var(--oh-border)"),
                               background: siteChoisi === st.id
                                 ? "var(--oh-accent-bg)" : "transparent",
                               color: siteChoisi === st.id
                                 ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
                {[st.nom_site, st.adresse, st.ville].filter(Boolean).join(" — ")}
                <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.8 }}>
                  {(st.dossiers || []).length} dossier(s)
                </span>
              </button>
            ))}
            <button type="button" role="radio" aria-checked={siteChoisi === "nouveau"}
                    onClick={() => setSiteChoisi("nouveau")}
                    data-testid="core-site-nouveau"
                    style={{ textAlign: "left", minHeight: 36, borderRadius: 8,
                             padding: "4px 10px",
                             border: "1px solid " + (siteChoisi === "nouveau"
                               ? "var(--oh-accent)" : "var(--oh-border)"),
                             background: siteChoisi === "nouveau"
                               ? "var(--oh-accent-bg)" : "transparent",
                             color: siteChoisi === "nouveau"
                               ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
              Nouvelle adresse d'intervention
            </button>
          </div>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {(!siteRequis || siteChoisi === "nouveau") && (<>
          <BanAddressInput value={site.adresse} testId="core-site-adresse"
            placeholder="Adresse d'intervention (autocomplétée)"
            onChange={(v) => setSite({ ...site, adresse: v })}
            onPick={({ rue, cp, ville }) => setSite({
              adresse: rue, code_postal: cp || site.code_postal,
              ville: ville || site.ville })} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr",
                        gap: 8 }}>
            {champ(site.code_postal, (v) => setSite({ ...site, code_postal: v }),
                   "Code postal", "core-site-cp")}
            {champ(site.ville, (v) => setSite({ ...site, ville: v }),
                   "Ville", "core-site-ville")}
          </div>
          </>)}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes (facultatif)" rows={2}
                    aria-label="Notes" data-testid="core-notes"
                    style={{ borderRadius: 8, padding: 10,
                             border: "1px solid var(--oh-border)",
                             background: "var(--oh-bg-surface)",
                             color: "var(--oh-text)", width: "100%" }} />
        </div>
      </div>
      )}

      {!creationClient && (
      <button onClick={creer} disabled={!peutCreerDossier}
              data-testid="core-creer"
              style={{ height: 40, borderRadius: 8, width: "100%",
                       border: "none", fontWeight: 500,
                       background: peutCreerDossier
                         ? "var(--oh-accent-solid)" : "var(--oh-bg-surface)",
                       color: peutCreerDossier
                         ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)" }}>
        {occupe ? <Loader2 size={16} className="animate-spin" />
                : "Créer le dossier"}
      </button>
      )}
      <div style={{ color: "var(--oh-text-secondary)", fontSize: 12,
                    marginTop: 10, lineHeight: 1.6 }}>
        Les interventions (visites, rapports, devis) se lancent
        ensuite depuis le dossier.
      </div>
    </div>
  );
}
